import type { PurchaseItemResponse } from "@9amhealth/openapi";
import {
  GetSuggestedTreatmentPlanResponse,
  SubscriptionControllerService,
  SubscriptionDetailsResponse,
  TreatmentPlanControllerService
} from "@9amhealth/openapi";
import { Cubit } from "blac";
import asTemplateValue from "lib/asTemplateValue";
import FunnelKey from "src/constants/funnelKey";
import { globalEvents } from "src/constants/globalEvents";
import {
  SUBSCRIPTION_PLAN_ID_MEDICARE,
  SUBSCRIPTION_PLAN_ID_PRESCRYPTIVE
} from "src/constants/misc";
import { PayerId } from "src/constants/payers";
import { TEST_STRIPS_SKU } from "src/constants/purchaseItemsSku";
import { dateLocal } from "src/lib/date";
import type { ParsedPurchaseItemResponse } from "src/lib/parsePurchaseItem";
import parsePurchaseItem from "src/lib/parsePurchaseItem";
import reportErrorSentry from "src/lib/reportErrorSentry";
import { LoadingKey } from "src/state/LoadingCubit/LoadingCubit";
import { KnownProgram } from "src/state/ProgramBloc/ProgramBloc";
import type { SubscriptionState } from "src/state/SubscriptionCubit/SubscriptionState";
import {
  ItemType,
  SubscriptionInterval
} from "src/state/TreatmentPlanCubit/TreatmentPlanCubit";
import {
  MemberPopulationType,
  ProfileAttributesKeys
} from "src/state/UserCubit/UserCubit";
import {
  apiMiddleware,
  appAccessState,
  loadingState,
  tracker,
  userState
} from "src/state/state";
import { StorageController } from "../StorageBloc/StorageBloc";

export type SubscriptionId = string | (() => string | undefined) | undefined;

export default class SubscriptionCubit extends Cubit<SubscriptionState> {
  isDemoFunnel = false;
  constructor() {
    super({});

    window.addEventListener(globalEvents.USER_CLEAR, () => {
      this.emit({});
    });

    window.addEventListener(
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      globalEvents.SET_CREDENTIALS as any,
      (event: CustomEvent) => {
        // Only clear state when credentials are actually provided (external login)
        if (!event.detail) {
          return;
        }
        this.emit({}); // Clear stale cache on new login
      }
    );

    const cached =
      StorageController.activeUserId &&
      StorageController.getItem(this.cacheKey);
    if (cached) {
      this.emit(JSON.parse(cached) as SubscriptionState);
    }
  }

  cacheKey = "subscriptions";
  emitAndCache = (state: SubscriptionState): void => {
    this.emit(state);
    if (StorageController.activeUserId) {
      StorageController.setItem(this.cacheKey, JSON.stringify(state));
    }
  };

  reset = (): void => {
    this.emitAndCache({});
  };

  setDemoFunnel = (isDemoFunnel: boolean): void => {
    this.isDemoFunnel = isDemoFunnel;
  };

  public readonly getFullSubscription = (
    status?: SubscriptionDetailsResponse.status[]
  ): SubscriptionDetailsResponse | undefined => {
    const filtered = this.filterAllSubscriptions({
      duration: [SubscriptionInterval.monthly, SubscriptionInterval.quarterly],
      status
    });
    return filtered[0] as SubscriptionDetailsResponse | undefined;
  };

  public readonly getSubscriptionById = (
    subscriptionId: SubscriptionId
  ): SubscriptionDetailsResponse | undefined => {
    if (!subscriptionId) {
      return;
    }
    const subId =
      typeof subscriptionId === "function" ? subscriptionId() : subscriptionId;
    const allSubs = this.state.allSubscriptions ?? [];
    const match = allSubs.find((subscription) => subscription.id === subId);
    // typecast SubscriptionDetailsResponse to SubscriptionDetailsResponse
    return match;
  };

  readonly getItemBySku = (
    subscriptionId: SubscriptionId,
    sku: string
  ): PurchaseItemResponse | undefined => {
    if (!subscriptionId) {
      return;
    }
    const subscription = this.getSubscriptionById(subscriptionId);
    if (!subscription?.purchaseItems) {
      return;
    }
    return subscription.purchaseItems.find((i) => i.sku === sku);
  };

  readonly fetchAndFilterSubscriptions = async (
    filter: {
      oneTime?: boolean;
      durations?: SubscriptionInterval[];
      status?: SubscriptionDetailsResponse.status[];
      items?: string[];
      ignoreItems?: string[];
      planIds?: string[];
      looseCheck?: boolean;
    } = {}
  ): Promise<SubscriptionDetailsResponse | undefined> => {
    loadingState.start(LoadingKey.subscriptionState);
    let subscription: SubscriptionDetailsResponse | undefined;
    try {
      await this.loadAllSubscriptions();
      const list = this.state.allSubscriptions ?? [];
      if (list.length === 0) {
        loadingState.finish(LoadingKey.subscriptionState);

        return;
      }

      const items = this.filterAllSubscriptions({
        duration:
          // use filter.durations first, if undefined use default durations
          filter.durations?.length
            ? filter.durations
            : filter.oneTime
              ? [SubscriptionInterval.oneTime]
              : [SubscriptionInterval.monthly, SubscriptionInterval.quarterly],
        status: filter.status,
        items: filter.items,
        ignoreItems: filter.ignoreItems,
        planIds: filter.planIds,
        looseCheck: filter.looseCheck
      });

      // newest subscription from all subscriptions
      items.sort((a, b) => {
        return dateLocal(a.createdDate).unix() < dateLocal(b.createdDate).unix()
          ? -1
          : 1;
      });

      subscription = items[0] as SubscriptionDetailsResponse | undefined;
    } catch (e: unknown) {
      reportErrorSentry(e);
    }
    loadingState.finish(LoadingKey.subscriptionState);
    return subscription;
  };

  readonly knownTreatmentPlanTargets = {
    CDC_DPP: KnownProgram.CDC_DPP
  } as const;
  readonly checkTreatmentTargetEligibility = async (
    target: string
  ): Promise<GetSuggestedTreatmentPlanResponse | undefined> => {
    loadingState.start(LoadingKey.subscriptionState);
    let eligibility: GetSuggestedTreatmentPlanResponse | undefined;
    try {
      const response =
        await TreatmentPlanControllerService.suggestTreatmentPlanForTarget(
          target
        );
      eligibility = response.data;
    } catch (error: unknown) {
      reportErrorSentry(error);
    }
    loadingState.finish(LoadingKey.subscriptionState);
    return eligibility;
  };

  private loadAllSubscriptionsPromise:
    | Promise<SubscriptionDetailsResponse[]>
    | undefined;

  readonly loadAllSubscriptions = async (
    options: { cachedSubscriptionsAcceptable?: boolean } = {}
  ): Promise<void> => {
    if (
      options.cachedSubscriptionsAcceptable &&
      typeof this.state.allSubscriptions !== "undefined"
    ) {
      return;
    }

    let promise = this.loadAllSubscriptionsPromise;

    if (!promise) {
      promise = new Promise((resolve, reject) => {
        void SubscriptionControllerService.listSubscriptions()
          .then((d) => resolve(d.data))
          .catch((e: unknown) => reject(e as Error))
          .finally(() => {
            this.loadAllSubscriptionsPromise = undefined;
          });
      });
      this.loadAllSubscriptionsPromise = promise;
    }

    try {
      const response = await promise;

      // sort, newest first
      response.sort(
        (a, b) =>
          new Date(b.createdDate).getTime() - new Date(a.createdDate).getTime()
      );

      const isSame =
        JSON.stringify(this.state.allSubscriptions) ===
        JSON.stringify(response);
      if (isSame) {
        return;
      }

      const newState: SubscriptionState = {
        ...this.state,
        allSubscriptions: response,
        hiddenItems: []
      };

      this.emitAndCache(newState);
    } catch (error: unknown) {
      reportErrorSentry(error);
    }
  };

  readonly filterAllSubscriptions = (filter: {
    status?: SubscriptionDetailsResponse.status[];
    duration?: SubscriptionInterval[];
    items?: string[];
    ignoreItems?: string[];
    planIds?: string[];
    metadataPartnerFunnel?: string[];
    metadataPayerId?: PayerId;
    /**
     * If looseCheck is true, the response can include subscriptions
     * which contain at least the SKUs defined in
     * "items", but can include more
     * -- Ignores: "ignoreItems"
     */
    looseCheck?: boolean;
  }): SubscriptionDetailsResponse[] => {
    const list = this.state.allSubscriptions ?? [];

    // sort
    list.sort((a, b) => {
      return dateLocal(a.createdDate).unix() < dateLocal(b.createdDate).unix()
        ? -1
        : 1;
    });

    // filter
    return list.filter((subscription) => {
      const matchStatus = filter.status
        ? filter.status.indexOf(subscription.status) !== -1
        : true;

      const matchDuration = filter.duration
        ? filter.duration.indexOf(
            subscription.renewalInterval as SubscriptionInterval
          ) !== -1
        : true;

      let itemsMatch = true;
      let planIdMatch = true;

      const filterItems = filter.items;
      if (filterItems) {
        const ignoreItems: string[] = [];
        const { purchaseItems } = subscription;

        if (filter.ignoreItems) {
          ignoreItems.push(...filter.ignoreItems);
        }

        if (filter.looseCheck) {
          const itemsIncludedInSubscription = purchaseItems
            .filter((item) => filterItems.includes(item.sku))
            .map((item) => {
              return item.sku;
            });

          itemsMatch =
            itemsIncludedInSubscription.length === filterItems.length;
        } else {
          const purchaseItemsFiltered = purchaseItems.filter(
            (i) => !ignoreItems.includes(i.sku as unknown as string)
          );

          const itemsMatchFilter = purchaseItemsFiltered.map((purchaseItem) => {
            return (
              filterItems.indexOf(purchaseItem.sku as unknown as string) !== -1
            );
          });

          itemsMatch = !itemsMatchFilter.some((itemMatch) => !itemMatch);
        }
      }

      const filterPlanIds = filter.planIds;
      if (filterPlanIds) {
        planIdMatch = filterPlanIds.includes(subscription.planId ?? "");
      }

      let matchMetadata = true;

      // filter for funnel field
      const filterMetadataPartnerFunnel = filter.metadataPartnerFunnel;
      if (filterMetadataPartnerFunnel) {
        matchMetadata = subscription.funnel
          ? filterMetadataPartnerFunnel.includes(subscription.funnel)
          : false;
      }

      // filter for metadata payerId
      const filterMetadataPayerId = filter.metadataPayerId;
      if (filterMetadataPayerId) {
        const { purchaseItems } = subscription;

        matchMetadata = purchaseItems.some(
          (purchaseItem) =>
            filterMetadataPayerId ===
            (asTemplateValue(purchaseItem.metadata["partner.payer"]) as
              | PayerId
              | string)
        );
      }

      return (
        matchStatus &&
        matchDuration &&
        itemsMatch &&
        planIdMatch &&
        matchMetadata
      );
    });
  };

  get hasActiveSubscription(): boolean | undefined {
    if (this.state.allSubscriptions === undefined) {
      return undefined;
    }

    const allowedStates = [
      SubscriptionDetailsResponse.status.ACTIVE,
      SubscriptionDetailsResponse.status.PAUSED,
      SubscriptionDetailsResponse.status.IN_REVIEW
    ];
    return (
      this.filterAllSubscriptions({
        status: allowedStates
      }).length > 0
    );
  }

  get hasEverHadSubscription(): boolean | undefined {
    if (this.state.allSubscriptions === undefined) {
      return undefined;
    }

    const allowedStates = [
      SubscriptionDetailsResponse.status.ACTIVE,
      SubscriptionDetailsResponse.status.PAUSED,
      SubscriptionDetailsResponse.status.IN_REVIEW,
      SubscriptionDetailsResponse.status.REJECTED,
      SubscriptionDetailsResponse.status.FINISHED
    ];
    return (
      this.filterAllSubscriptions({
        status: allowedStates
      }).length > 0
    );
  }

  get hasInactiveSubscription(): boolean | undefined {
    if (this.state.allSubscriptions === undefined) {
      return undefined;
    }

    const allowedStates = [
      SubscriptionDetailsResponse.status.PAUSED,
      SubscriptionDetailsResponse.status.REJECTED,
      SubscriptionDetailsResponse.status.FINISHED
    ];
    return (
      this.filterAllSubscriptions({
        status: allowedStates
      }).length > 0
    );
  }

  get hasFinishedSubscription(): boolean | undefined {
    if (this.state.allSubscriptions === undefined) {
      return undefined;
    }
    return (
      this.filterAllSubscriptions({
        status: [SubscriptionDetailsResponse.status.FINISHED]
      }).length > 0
    );
  }

  readonly buySubscription = async (
    subscriptionId: SubscriptionId,
    options: {
      onError?: (e?: Error | unknown) => void;
      onSuccess?: () => void;
    } = {}
  ): Promise<boolean> => {
    if (!subscriptionId) {
      return false;
    }
    let success = false;
    apiMiddleware.clearAll();
    const subscription = this.getSubscriptionById(subscriptionId);
    const id = subscription?.id;
    if (!id) {
      return false;
    }

    try {
      loadingState.start(LoadingKey.treatmentPlan);

      await SubscriptionControllerService.buySubscription(id);

      if (this.isDemoFunnel) {
        await userState.populateUserprofileWithDemoData();
      }

      await this.getSubscriptionDetails(id);

      try {
        await appAccessState.evaluateAccess();
      } catch (accessError: unknown) {
        reportErrorSentry(accessError);
      }

      options.onSuccess?.();
      success = true;
    } catch (e: unknown) {
      reportErrorSentry(e);
      options.onError?.(e);
      success = false;
    }

    loadingState.finish(LoadingKey.treatmentPlan);
    apiMiddleware.clearAll();
    return success;
  };

  readonly getSubscriptionDetails = async (
    subscriptionId: string
  ): Promise<void> => {
    loadingState.start(LoadingKey.treatmentPlan);
    try {
      const details =
        await SubscriptionControllerService.subscriptionDetails(subscriptionId);
      this.insertSubscription(details.data);
    } catch (e: unknown) {
      reportErrorSentry(e);
    }
    loadingState.finish(LoadingKey.treatmentPlan);
  };

  readonly getSubscriptionPurchaseItems = (
    subscriptionId: SubscriptionId,
    itemType: ItemType | ItemType[]
  ): PurchaseItemResponse[] => {
    if (!subscriptionId) {
      return [];
    }
    if (!Array.isArray(itemType)) {
      itemType = [itemType];
    }
    const subscription = this.getSubscriptionById(subscriptionId);
    return (subscription?.purchaseItems ?? [])
      .filter((item) =>
        itemType.includes(item.externalItemType as unknown as ItemType)
      )
      .sort((a, b) => {
        // sort alphabetically by sku
        return b.sku < a.sku ? -1 : 1;
      });
  };

  readonly insertSubscription = (
    subscription: SubscriptionDetailsResponse
  ): void => {
    const currentList = this.state.allSubscriptions ?? [];

    let replacedCurrent = false;
    const allSubscriptions: SubscriptionDetailsResponse[] = [];

    for (const sub of currentList) {
      if (sub.id === subscription.id) {
        replacedCurrent = true;
        allSubscriptions.push(subscription);
      } else {
        allSubscriptions.push(sub);
      }
    }

    if (!replacedCurrent) {
      allSubscriptions.push(subscription);
    }

    this.emitAndCache({
      ...this.state,
      allSubscriptions
    });
  };

  readonly getParsedMeds = (
    subscriptionId: SubscriptionId
  ): ParsedPurchaseItemResponse[] => {
    if (!subscriptionId) {
      return [];
    }
    return [
      ...this.getSubscriptionPurchaseItems(
        subscriptionId,
        ItemType.MEDICATION_SUGGESTION
      ),
      ...this.getSubscriptionPurchaseItems(
        subscriptionId,
        ItemType.PRESCRIBED_MEDICATION
      ),
      ...this.getSubscriptionPurchaseItems(
        subscriptionId,
        ItemType.PRELIMINARY_MEDICATION_SUGGESTION
      ),
      ...this.getSubscriptionPurchaseItems(
        subscriptionId,
        ItemType.prescription
      )
    ].map(parsePurchaseItem);
  };

  readonly getParsedSupplies = (
    subscriptionId: SubscriptionId
  ): ParsedPurchaseItemResponse[] => {
    if (!subscriptionId) {
      return [];
    }
    return [
      ...this.getSubscriptionPurchaseItems(
        subscriptionId,
        ItemType.PRESCRIBED_SUPPLY
      )
    ].map(parsePurchaseItem);
  };

  readonly checkHasLabs = (
    subscriptionId: SubscriptionId
  ): PurchaseItemResponse | undefined => {
    if (!subscriptionId) {
      return;
    }
    return this.getSubscriptionPurchaseItems(
      subscriptionId,
      ItemType.LAB_TEST_SUGGESTION
    )[0] as PurchaseItemResponse | undefined;
  };

  readonly checkHasTestStrips = (): PurchaseItemResponse | undefined => {
    const allowedStates = [
      SubscriptionDetailsResponse.status.ACTIVE,
      SubscriptionDetailsResponse.status.IN_REVIEW
    ];

    const subscription = this.filterAllSubscriptions({
      status: allowedStates,
      items: [TEST_STRIPS_SKU],
      looseCheck: true
    })[0] as SubscriptionDetailsResponse | undefined;

    return this.getSubscriptionPurchaseItems(
      subscription?.id,
      ItemType.OVER_THE_COUNTER_STRIP_ITEM
    )[0] as PurchaseItemResponse | undefined;
  };

  readonly checkHasPlan = (
    subscriptionId: SubscriptionId
  ): PurchaseItemResponse | undefined => {
    if (!subscriptionId) {
      return;
    }
    return this.getSubscriptionPurchaseItems(
      subscriptionId,
      ItemType.SUBSCRIPTION_PLAN_SUGGESTION
    )[0] as PurchaseItemResponse | undefined;
  };

  readonly checkHasPlaceholderMedications = (
    subscriptionId: SubscriptionId
  ): boolean => {
    if (!subscriptionId) {
      return false;
    }

    return (
      this.getSubscriptionPurchaseItems(
        subscriptionId,
        ItemType.PRELIMINARY_MEDICATION_SUGGESTION
      ).length > 0
    );
  };

  get coveredByInsurance(): boolean | undefined {
    if (this.state.allSubscriptions === undefined) {
      return undefined;
    }

    const coveredPlanIds = [
      SUBSCRIPTION_PLAN_ID_PRESCRYPTIVE,
      SUBSCRIPTION_PLAN_ID_MEDICARE
    ];

    const hasCoveredPlan = this.filterAllSubscriptions({
      status: [
        SubscriptionDetailsResponse.status.ACTIVE,
        SubscriptionDetailsResponse.status.IN_REVIEW
      ],
      looseCheck: true
    }).some((subscription) => coveredPlanIds.includes(subscription.planId));

    return hasCoveredPlan;
  }

  private getEligibilityForCurrentPartnerPromise: Promise<GetSuggestedTreatmentPlanResponse | null> | null =
    null;

  readonly getEligibilityForCurrentPartner =
    async (): Promise<GetSuggestedTreatmentPlanResponse | null> => {
      let promise = this.getEligibilityForCurrentPartnerPromise;

      if (!promise) {
        promise = new Promise<GetSuggestedTreatmentPlanResponse | null>(
          (resolve, reject) => {
            void TreatmentPlanControllerService.suggestTreatmentPlan()
              .then((response) => {
                resolve(response.data);
              })
              .catch((e: unknown) => {
                reject(e as Error);
              })
              .finally(() => {
                this.getEligibilityForCurrentPartnerPromise = null;
              });
          }
        );
        this.getEligibilityForCurrentPartnerPromise = promise;
      }
      try {
        return await promise;
      } catch (e) {
        reportErrorSentry(e);
        return null;
      }
    };

  extractPayerInfo = (
    filter: {
      status?: SubscriptionDetailsResponse.status[];
    } = {}
  ): {
    lastSignupFunnel?: FunnelKey;
    payerIsCashPay?: boolean;
  } => {
    const activeSub = this.filterAllSubscriptions({
      status: filter.status ?? [SubscriptionDetailsResponse.status.ACTIVE],
      looseCheck: true
    });
    const draftSub = this.filterAllSubscriptions({
      status: filter.status ?? [SubscriptionDetailsResponse.status.DRAFT],
      looseCheck: true
    });

    const useSub = [...activeSub, ...draftSub];

    let lastSignupFunnel: FunnelKey | undefined = undefined;
    let payerIsCashPay: boolean | undefined = undefined;
    useSub.forEach((sub) => {
      if (sub.funnel) {
        lastSignupFunnel = sub.funnel as FunnelKey;
      }

      for (const item of sub.purchaseItems) {
        if (item.metadata["partner.payer"]) {
          payerIsCashPay =
            asTemplateValue(item.metadata["partner.payer"]) ===
            PayerId.CASH_PAY;
        }
      }

      // Fallback logic for cash pay subscriptions without funnel
      if (!lastSignupFunnel && payerIsCashPay) {
        const isActiveSub =
          sub.status === SubscriptionDetailsResponse.status.ACTIVE;
        if (!isActiveSub) {
          lastSignupFunnel = FunnelKey.universal;
        }
      }
    });

    return {
      lastSignupFunnel,
      payerIsCashPay
    };
  };

  get activeSubscriptionPayerId(): PayerId | string | undefined {
    const activeSub = this.getFullSubscription([
      SubscriptionDetailsResponse.status.ACTIVE
    ]);

    if (!activeSub) {
      return undefined;
    }

    const partnerPayerId = activeSub.purchaseItems.find((item) =>
      Boolean(item.metadata["partner.payer"])
    )?.metadata["partner.payer"] as PayerId | string | undefined;

    return partnerPayerId;
  }

  get activeSubscriptionSupportsHybridApp(): boolean {
    const partnerPayerId = this.activeSubscriptionPayerId;

    // yes if no payerId is found
    if (!partnerPayerId) {
      return true;
    }

    const payerIdsNotSupported: PayerId[] = [PayerId.TRANSCARENT] as const;

    // yes if payerId is not in the list of unsupported payerIds, no otherwise
    return !payerIdsNotSupported.includes(partnerPayerId as PayerId);
  }

  get activeSubscriptionHasBranding(): PayerId | false {
    const partnerPayerId = this.activeSubscriptionPayerId as
      | PayerId
      | undefined;
    if (!partnerPayerId) {
      return false;
    }
    const payerIdsWithBranding: (PayerId | "")[] = [""] as const;
    const hasBranding = payerIdsWithBranding.includes(partnerPayerId);
    return hasBranding ? partnerPayerId : false;
  }

  get activeSubscriptionDetails(): {
    partnerFunnel?: FunnelKey;
    cashPay?: boolean;
    dtc?: boolean;
  } {
    const activeSub = this.getFullSubscription([
      SubscriptionDetailsResponse.status.ACTIVE
    ]);

    if (!activeSub) {
      return {};
    }

    const employer = activeSub.purchaseItems
      .find((item) => Boolean(item.metadata["partner.employer"]))
      ?.sku.split(":")
      .pop() as FunnelKey | undefined;

    const partnerFunnel = activeSub.funnel as FunnelKey | undefined;

    const cashPay =
      asTemplateValue(
        activeSub.purchaseItems.find((item) =>
          Boolean(item.metadata["partner.payer"])
        )?.metadata["partner.payer"]
      ) === PayerId.CASH_PAY;

    const { profileAttributes } = userState.state;
    const dtc =
      profileAttributes?.[ProfileAttributesKeys.memberPopulation].type ===
      MemberPopulationType.DTC;

    tracker.track("Callback Request Availability Information", {
      data: {
        employer: employer,
        partnerFunnel: partnerFunnel,
        isDtc: dtc,
        cashPay: cashPay
      }
    });

    return {
      partnerFunnel: employer ?? partnerFunnel,
      cashPay,
      dtc
    };
  }

  reserveAndApplyCoupon = async (coupon: string): Promise<void> => {
    this.emitAndCache({
      ...this.state,
      coupon
    });
    // reserve the coupon
    try {
      await SubscriptionControllerService.reserveCoupon({
        coupon
      });
    } catch (e) {
      reportErrorSentry(e);
    }
    // apply the coupon to the subscriptions
    void this.applyCouponToSubscriptions(coupon);
  };

  applyCouponToSubscriptions = async (coupon: string): Promise<void> => {
    const subscriptions = this.state.allSubscriptions;
    const cashPaySubs = subscriptions?.filter((sub) =>
      sub.purchaseItems.some(
        (item) =>
          // check if the subscription is cash pay
          Boolean(item.metadata["partner.payer"]) &&
          asTemplateValue(item.metadata["partner.payer"]) ===
            PayerId.CASH_PAY &&
          // check if the subscription already has the coupon
          !sub.appliedCoupons.some((c) => c.coupon === coupon)
      )
    );

    if (!cashPaySubs) {
      return;
    }

    try {
      let applied = false;
      for (const subscription of cashPaySubs) {
        // apply the coupon to the subscription
        await SubscriptionControllerService.applyCoupon(subscription.id, {
          coupon
        });
        applied = true;
      }
      if (applied) {
        void this.loadAllSubscriptions();
      }
    } catch (e) {
      reportErrorSentry(e);
    }
  };

  checkForCoupon = (): void => {
    const coupon = tracker.initialUtmParams?.coupon as string | undefined;
    if (coupon) {
      void this.reserveAndApplyCoupon(coupon);
    }
  };

  getSubscriptionsMatchingAppAccessCriteria = ():
    | SubscriptionDetailsResponse[]
    | undefined => {
    const allowedStates = [
      SubscriptionDetailsResponse.status.ACTIVE,
      SubscriptionDetailsResponse.status.PAUSED,
      SubscriptionDetailsResponse.status.FINISHED,
      SubscriptionDetailsResponse.status.IN_REVIEW,
      SubscriptionDetailsResponse.status.REJECTED
    ];

    const subscriptions = this.filterAllSubscriptions({
      status: allowedStates,
      looseCheck: true
    });

    return subscriptions;
  };
}
